import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Shield, Leaf, Award, Sparkles, Clock, ArrowRight, Italic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ContactForm from '../components/ContactForm';
import TestimonialsSlider from '../components/TestimonialsSlider';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const products = [
  {
    id: 'chilli-powder',
    name: 'Premium Chilli Powder',
    malayalamName: 'മുളകുപൊടി',
    description: 'Rich natural color, balanced heat, perfect for everyday South Indian cooking. Our signature spice that adds vibrant red hue and authentic flavor to your dishes.',
    features: ['Rich Natural Color', 'Balanced Heat', 'Fine Grind'],
    image: 'https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp',
    available: true
  },
  {
    id: 'coriander-powder',
    name: 'Coriander Powder',
    malayalamName: 'മല്ലിപ്പൊടി',
    description: 'Freshly ground coriander seeds with an earthy, citrusy aroma. Essential for curries, marinades, and authentic Indian gravies.',
    features: ['Aromatic & Fresh', 'Citrus Notes', 'Perfect for Gravies'],
    image: 'https://storage.googleapis.com/agniya-images/website/images/Coriander%20Powder.webp',
    available: true
  },
  {
    id: 'turmeric-powder',
    name: 'Turmeric Powder',
    malayalamName: 'മഞ്ഞൾപ്പൊടി',
    description: 'Golden turmeric with high curcumin content. Adds beautiful color and subtle earthy flavor to all your dishes while providing natural health benefits.',
    features: ['High Curcumin', 'Golden Color', 'Natural & Pure'],
    image: 'https://storage.googleapis.com/agniya-images/website/images/Turmeric%20Powder.webp',
    available: true
  },
  {
    id: 'kashmiri-chilli-powder',
    name: 'Kashmiri Chilli Powder',
    malayalamName: 'കാശ്മീരി മുളകുപൊടി',
    description: 'Prized for its brilliant red color with mild heat. Perfect when you want vibrant color without overpowering spice. Ideal for biriyani, tandoori, and restaurant-style dishes.',
    features: ['Vibrant Red Color', 'Mild Heat', 'Premium Quality'],
    image: 'https://storage.googleapis.com/agniya-images/website/images/Kashmirii%20Chilli%20Powder.webp',
    available: true
  },
  {
    id: 'garam-masala',
    name: 'Garam Masala',
    malayalamName: 'ഗരം മസാല',
    description: 'A perfect blend of aromatic spices including cardamom, cinnamon, cloves, and more. The finishing touch that elevates any Indian dish.',
    features: ['Aromatic Blend', 'Premium Spices', 'Perfect Balance'],
    image: 'https://storage.googleapis.com/agniya-images/Garam_Masala.png',
    available: false,
    comingSoon: true
  },
  {
    id: 'dried-spices',
    name: 'Dried Whole Spices',
    malayalamName: 'ഉണങ്ങിയ സുഗന്ധവ്യഞ്ജനങ്ങൾ',
    description: 'Premium selection of whole dried spices including cardamom, cloves, cinnamon sticks, and bay leaves. Perfect for tempering and slow-cooked dishes.',
    features: ['Whole Spices', 'Premium Quality', 'Long Lasting Aroma'],
    image: 'https://storage.googleapis.com/agniya-images/Whole_Dried_Spices.png',
    available: false,
    comingSoon: true
  }
];

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle scroll to section when navigating from another page
  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        // Small delay to ensure page is rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      // Clear the state to prevent re-scrolling on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Agniya Spices | Pure & Trusted Indian Spices</title>
        <meta 
          name="description" 
          content="Buy premium Indian spices including chilli powder, turmeric, coriander and more. Pure ingredients, hygienic packing, and trusted quality." 
        />
        <meta 
          name="keywords" 
          content="Agniya Spices, Indian spices, chilli powder, turmeric powder, coriander powder, garam masala, Kerala spices, South Indian spices, pure spices, premium spices, Kochi spices, restaurant spices, catering spices" 
        />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Agniya Spices | Pure & Trusted Indian Spices" />
        <meta 
          property="og:description" 
          content="Buy premium Indian spices including chilli powder, turmeric, coriander and more. Pure ingredients, hygienic packing, and trusted quality." 
        />
        <meta property="og:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />
        <meta property="og:url" content="https://agniyaspices.com" />
        <meta property="og:site_name" content="Agniya Spices" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Agniya Spices | Pure & Trusted Indian Spices" />
        <meta 
          name="twitter:description" 
          content="Buy premium Indian spices including chilli powder, turmeric, coriander and more. Pure ingredients, hygienic packing, and trusted quality." 
        />
        <meta name="twitter:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Agniya Spices" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="geo.region" content="IN-KL" />
        <meta name="geo.placename" content="Kodungallur, Kerala" />
        <meta name="geo.position" content="9.9312;76.2673" />
        <link rel="canonical" href="https://agniyaspices.com" />
        
        {/* Organization Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Agniya Spices",
            "url": "https://agniyaspices.com",
            "logo": "https://agniyaspices.com/favicon.ico",
            "description": "Premium Indian spices manufacturer and supplier based in Kerala, India",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "Kerala",
              "addressCountry": "IN"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8086075558",
              "contactType": "Customer Service"
            },
            "sameAs": [
              "https://agniyaspices.com"
            ]
          })}
        </script>

        {/* WebSite Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Agniya Spices",
            "url": "https://agniyaspices.com",
            "description": "Buy premium Indian spices including chilli powder, turmeric, coriander and more.",
            "publisher": {
              "@type": "Organization",
              "name": "Agniya Spices"
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://agniyaspices.com/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/favicon.ico"
              alt="Agniya Spices logo"
              className="h-10 w-10 rounded-sm object-contain"
            />
            <div className="text-2xl font-bold text-[#8B2820]">
              AGNIYA SPICES
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <button onClick={() => scrollToSection('home')} className="text-[#4a3a33] hover:text-[#8B2820] transition-colors" data-testid="nav-home">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-[#4a3a33] hover:text-[#8B2820] transition-colors" data-testid="nav-about">About Us</button>
            
            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-[#4a3a33] hover:text-[#8B2820] transition-colors focus:outline-none" data-testid="nav-products">
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-[#8B2820]/20 shadow-lg">
                <DropdownMenuItem asChild>
                  <Link to="/products/chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Red Chilli Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/coriander-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Coriander Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/turmeric-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Turmeric Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/kashmiri-chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Kashmiri Chilli Powder
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/spices-guide" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors" data-testid="nav-spices-guide">Spice Guide</Link>
            <Link to="/testimonials" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors" data-testid="nav-testimonials">Testimonials</Link>
            <Link to="/faq" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors" data-testid="nav-faq">FAQ</Link>
            <button onClick={() => scrollToSection('quality')} className="text-[#4a3a33] hover:text-[#8B2820] transition-colors" data-testid="nav-quality">Quality</button>
            <Link to="/contact" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors" data-testid="nav-contact">Contact Us</Link>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-[#4a3a33]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <nav className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              <button onClick={() => scrollToSection('home')} className="text-left text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-left text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2">About Us</button>
              
              {/* Products Submenu */}
              <div className="flex flex-col space-y-2">
                <span className="text-[#4a3a33] font-semibold py-2">Products</span>
                <Link to="/products/chilli-powder" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                  Red Chilli Powder
                </Link>
                <Link to="/products/coriander-powder" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                  Coriander Powder
                </Link>
                <Link to="/products/turmeric-powder" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                  Turmeric Powder
                </Link>
                <Link to="/products/kashmiri-chilli-powder" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                  Kashmiri Chilli Powder
                </Link>
              </div>
              
              <Link to="/spices-guide" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>Spice Guide</Link>
              <Link to="/testimonials" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>Testimonials</Link>
              <Link to="/faq" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>FAQ</Link>
              <button onClick={() => scrollToSection('quality')} className="text-left text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2">Quality</button>
              <Link to="/contact" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>Contact Us</Link>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://storage.googleapis.com/agniya-images/website/images/bg_home.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#8B2820]/20 to-[#4a3a33]/40 z-0" />
        
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <h1 className="text-5xl sm:text-6xl md:text-8xl font-bold mb-6 animate-fade-in">
            AGNIYA SPICES
          </h1>
          <p className="text-2xl sm:text-3xl md:text-5xl mb-4 font-medium text-[#f7f5f2]">
            Not All Chilli Powders Are the Same
          </p>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 text-[#d4ccc4] font-light">
            Choose the right spice for perfect color and taste
          </p>
          <p className="text-md sm:text-lg md:text-xl mb-12 text-[#d4ccc4] font-light italic">
            ഓരോ തരിയിലും ശുദ്ധിയും വിശ്വാസവും
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => scrollToSection('products')}
              size="lg"
              className="bg-[#8B2820] hover:bg-[#6d1f19] text-white px-8 sm:px-12 py-5 sm:py-6 text-lg sm:text-xl rounded-md shadow-2xl transition-all duration-300 hover:scale-105"
              data-testid="hero-cta-btn"
            >
              <Sparkles className="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
              Explore Our Products
            </Button>
            <Link to="/spices-guide">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/20 px-8 sm:px-12 py-5 sm:py-6 text-lg sm:text-xl rounded-md shadow-2xl transition-all duration-300 hover:scale-105"
                data-testid="hero-guide-cta-btn"
              >
                Explore Spice Guide
                <ArrowRight className="ml-2 h-5 w-5 sm:h-6 sm:w-6" />
              </Button>
            </Link>
          </div>
          
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown className="h-10 w-10 text-white" />
          </div>
        </div>
      </section>

      {/* About the Brand */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-8 text-center">
              About Agniya Spices
            </h2>
            <div className="prose prose-lg max-w-none text-[#4a3a33] leading-relaxed text-center">
              <p className="text-lg sm:text-xl mb-6">
                At Agniya Spices, we believe that great cooking begins with pure ingredients. 
                Our journey is rooted in a commitment to purity and trust, carefully sourcing 
                the finest spices to bring authentic flavor to your kitchen.
              </p>
              <p className="text-base sm:text-lg" style={{ fontStyle: 'italic' }}>
                Through fine grinding and hygienic packing processes, we ensure that every 
                pinch of our spices meets the highest standards of quality. We understand the 
                importance of reliability in everyday cooking, which is why households and 
                professional kitchens across South India trust Agniya Spices for their culinary needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Agniya Spices */}
      <section className="py-20 bg-[#f7f5f2]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-16 text-center">
            Why Choose Agniya Spices
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-[#8B2820]/10 rounded-full">
                    <Leaf className="h-10 w-10 sm:h-12 sm:w-12 text-[#8B2820]" />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#4a3a33] mb-4">Pure Ingredients</h3>
                <p className="text-sm sm:text-base text-[#736c64]">
                  Only the finest, naturally sourced spices with no additives or preservatives.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-[#8B2820]/10 rounded-full">
                    <Shield className="h-10 w-10 sm:h-12 sm:w-12 text-[#8B2820]" />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#4a3a33] mb-4">No Added Colour</h3>
                <p className="text-sm sm:text-base text-[#736c64]">
                  Rich, natural color from premium spices without any artificial coloring.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-[#8B2820]/10 rounded-full">
                    <Award className="h-10 w-10 sm:h-12 sm:w-12 text-[#8B2820]" />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#4a3a33] mb-4">Fine Grind</h3>
                <p className="text-sm sm:text-base text-[#736c64]">
                  Expertly ground for better color dispersion and authentic taste in every dish.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-[#8B2820]/10 rounded-full">
                    <Sparkles className="h-10 w-10 sm:h-12 sm:w-12 text-[#8B2820]" />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#4a3a33] mb-4">Hygienically Packed</h3>
                <p className="text-sm sm:text-base text-[#736c64]">
                  Processed and packed in modern facilities maintaining strict hygiene standards.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Choose by Need Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-4">
              Choose Spices for Your Need
            </h2>
            <p className="text-lg text-[#4a3a33] max-w-2xl mx-auto">
              Find the perfect spice blend for your specific cooking requirements
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/spices-guide" className="group">
              <Card className="border-2 border-[#8B2820]/20 hover:border-[#8B2820] shadow-md hover:shadow-xl transition-all duration-300 bg-[#f7f5f2] h-full">
                <CardContent className="p-6 text-center flex flex-col h-full">
                  <div className="text-4xl mb-4">🍛</div>
                  <h3 className="text-xl font-bold text-[#4a3a33] mb-2 group-hover:text-[#8B2820] transition-colors">
                    For Biriyani
                  </h3>
                  <p className="text-sm text-[#736c64] flex-grow">
                    Rich color and balanced heat for perfect biriyani
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/spices-guide" className="group">
              <Card className="border-2 border-[#8B2820]/20 hover:border-[#8B2820] shadow-md hover:shadow-xl transition-all duration-300 bg-[#f7f5f2] h-full">
                <CardContent className="p-6 text-center flex flex-col h-full">
                  <div className="text-4xl mb-4">🌶️</div>
                  <h3 className="text-xl font-bold text-[#4a3a33] mb-2 group-hover:text-[#8B2820] transition-colors">
                    For Spicy Cooking
                  </h3>
                  <p className="text-sm text-[#736c64] flex-grow">
                    High heat varieties for authentic spicy dishes
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/spices-guide" className="group">
              <Card className="border-2 border-[#8B2820]/20 hover:border-[#8B2820] shadow-md hover:shadow-xl transition-all duration-300 bg-[#f7f5f2] h-full">
                <CardContent className="p-6 text-center flex flex-col h-full">
                  <div className="text-4xl mb-4">🏠</div>
                  <h3 className="text-xl font-bold text-[#4a3a33] mb-2 group-hover:text-[#8B2820] transition-colors">
                    For Daily Use
                  </h3>
                  <p className="text-sm text-[#736c64] flex-grow">
                    Versatile spices for everyday home cooking
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/spices-guide" className="group">
              <Card className="border-2 border-[#8B2820]/20 hover:border-[#8B2820] shadow-md hover:shadow-xl transition-all duration-300 bg-[#f7f5f2] h-full">
                <CardContent className="p-6 text-center flex flex-col h-full">
                  <div className="text-4xl mb-4">🏪</div>
                  <h3 className="text-xl font-bold text-[#4a3a33] mb-2 group-hover:text-[#8B2820] transition-colors">
                    For Bulk
                  </h3>
                  <p className="text-sm text-[#736c64] flex-grow">
                    Consistent quality for restaurants & caterers
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-4">
              Our Products
            </h2>
            <p className="text-base sm:text-lg text-[#4a3a33] max-w-2xl mx-auto">
              Discover our range of premium Indian spices, crafted with care for authentic taste
            </p>
          </div>

          <div className="space-y-20">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                data-testid={`product-${product.id}`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820]">
                      {product.name}
                    </h3>
                    {product.comingSoon && (
                      <Badge className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1">
                        <Clock className="w-3 h-3 mr-1" />
                        Launching Soon
                      </Badge>
                    )}
                  </div>
                  <p className="text-lg sm:text-xl text-[#8B2820]/70 mb-4 font-medium">{product.malayalamName}</p>
                  <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-[#4a3a33]">
                    <p className="leading-relaxed">
                      {product.description}
                    </p>
                    <div className="space-y-3 sm:space-y-4">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <div className="w-2 h-2 bg-[#8B2820] rounded-full mt-2 mr-3 flex-shrink-0" />
                          <p><strong className="text-[#8B2820]">{feature}</strong></p>
                        </div>
                      ))}
                    </div>
                    {product.comingSoon && (
                      <p className="text-amber-600 font-medium text-sm sm:text-base bg-amber-50 p-3 rounded-lg inline-block">
                        We're perfecting this blend for you. Stay tuned!
                      </p>
                    )}
                    {/* View Details Button */}
                    <div className="pt-4">
                      <Link to={`/products/${product.id}`}>
                        <Button 
                          variant={product.comingSoon ? "outline" : "default"}
                          className={product.comingSoon 
                            ? "border-amber-500 text-amber-600 hover:bg-amber-50" 
                            : "bg-[#8B2820] hover:bg-[#6d1f19] text-white"
                          }
                          data-testid={`view-details-${product.id}`}
                        >
                          {product.comingSoon ? 'View Preview' : 'View Details'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="relative rounded-lg overflow-hidden shadow-2xl group">
                    <img 
                      src={product.image}
                      alt={`${product.name} - Pure Indian spice powder for cooking`}
                      loading="lazy"
                      width="800"
                      height="600"
                      className={`w-full h-64 sm:h-80 lg:h-96 object-cover transition-transform duration-500 group-hover:scale-105 ${product.comingSoon ? 'opacity-80' : ''}`}
                    />
                    {product.comingSoon && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-center pb-8">
                        <span className="text-white text-xl font-bold bg-amber-500 px-6 py-2 rounded-full">
                          Coming Soon
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Quality Section */}
      <section id="quality" className="py-20 bg-[#f7f5f2]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-16 text-center">
            Our Commitment to Quality
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="mb-6 flex justify-center">
                <img 
                  src="https://images.unsplash.com/photo-1766158554170-1e8b1899c8ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxkcmllZCUyMGNoaWxsaWVzfGVufDB8fHx8MTc2ODI0NDc0Mnww&ixlib=rb-4.1.0&q=85"
                  alt="Traditional Values"
                  className="w-full h-48 sm:h-64 object-cover rounded-lg shadow-lg"
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#4a3a33] mb-4">Traditional Values</h3>
              <p className="text-sm sm:text-base text-[#736c64]">
                Rooted in generations of spice expertise and traditional Indian culinary wisdom.
              </p>
            </div>

            <div className="text-center">
              <div className="mb-6 flex justify-center">
                <img 
                  src="https://images.unsplash.com/photo-1702041295471-01b73fd39907?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxyZWQlMjBjaGlsbGklMjBwb3dkZXJ8ZW58MHx8fHwxNzY4MjQ0NzM2fDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern Hygiene"
                  className="w-full h-48 sm:h-64 object-cover rounded-lg shadow-lg"
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#4a3a33] mb-4">Modern Hygiene Standards</h3>
              <p className="text-sm sm:text-base text-[#736c64]">
                State-of-the-art facilities ensuring the highest levels of cleanliness and safety.
              </p>
            </div>

            <div className="text-center">
              <div className="mb-6 flex justify-center">
                <img 
                  src="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxzcGljZXN8ZW58MHx8fHwxNzY4MjQ0NzQ3fDA&ixlib=rb-4.1.0&q=85"
                  alt="Consistency"
                  className="w-full h-48 sm:h-64 object-cover rounded-lg shadow-lg"
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#4a3a33] mb-4">Consistency & Reliability</h3>
              <p className="text-sm sm:text-base text-[#736c64]">
                Every batch meets our rigorous quality standards for uniform taste and color.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Future Vision Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8B2820] mb-8">
              Our Vision for Tomorrow
            </h2>
            <p className="text-lg sm:text-xl text-[#4a3a33] leading-relaxed mb-6">
              As we grow, we remain committed to expanding our range of authentic Indian spices 
              while staying rooted in the values of trust and quality that define Agniya Spices.
            </p>
            <p className="text-base sm:text-lg text-[#736c64] leading-relaxed">
              Whether serving home kitchens or professional culinary establishments, our mission 
              remains unchanged: to deliver pure, trusted spices that elevate every meal.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Slider Section */}
      <TestimonialsSlider />

      {/* Contact Form Section */}
      <ContactForm />

      <Footer onScrollTo={scrollToSection} copyrightYear={2026} />
    </div>
  );
};

export default HomePage;
